import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/config/api';

/**
 * Achievement definitions for Don Doit
 * Each achievement has: id, name, description, icon, condition checker
 */
export const ACHIEVEMENTS = [
  // === TIME TRACKING ===
  {
    id: 'prima_colazione',
    name: 'Prima Colazione',
    description: 'Hai loggato ore prima delle 9:00',
    icon: '🥐',
    category: 'time',
    check: (stats) => stats.earlyEntries > 0,
  },
  {
    id: 'nottambulo',
    name: 'Il Nottambulo',
    description: 'Hai loggato ore dopo le 22:00',
    icon: '🦉',
    category: 'time',
    check: (stats) => stats.lateEntries > 0,
  },
  {
    id: 'stakanovista',
    name: 'Stakanovista',
    description: 'Hai loggato più di 10 ore in un giorno',
    icon: '💪',
    category: 'time',
    check: (stats) => stats.maxHoursInDay >= 10,
  },
  {
    id: 'costante',
    name: 'Il Costante',
    description: 'Hai loggato ore per 5 giorni consecutivi',
    icon: '🔥',
    category: 'time',
    check: (stats) => stats.currentStreak >= 5,
  },
  {
    id: 'leggenda_streak',
    name: 'Leggenda dello Streak',
    description: 'Hai loggato ore per 20 giorni consecutivi',
    icon: '👑',
    category: 'time',
    check: (stats) => stats.currentStreak >= 20,
  },
  {
    id: 'centenario',
    name: 'Il Centenario',
    description: 'Hai superato 100 ore totali',
    icon: '💯',
    category: 'time',
    check: (stats) => stats.totalHours >= 100,
  },
  {
    id: 'mille_ore',
    name: 'Mille e una Notte',
    description: 'Hai superato 1000 ore totali',
    icon: '🌟',
    category: 'time',
    check: (stats) => stats.totalHours >= 1000,
  },

  // === TASKS ===
  {
    id: 'primo_task',
    name: 'Primo Incarico',
    description: 'Hai completato il tuo primo task',
    icon: '✅',
    category: 'tasks',
    check: (stats) => stats.completedTasks >= 1,
  },
  {
    id: 'soldato_affidabile',
    name: 'Soldato Affidabile',
    description: 'Hai completato 10 task',
    icon: '🎖️',
    category: 'tasks',
    check: (stats) => stats.completedTasks >= 10,
  },
  {
    id: 'braccio_destro',
    name: 'Braccio Destro',
    description: 'Hai completato 50 task',
    icon: '🦾',
    category: 'tasks',
    check: (stats) => stats.completedTasks >= 50,
  },
  {
    id: 'fulmine',
    name: 'Il Fulmine',
    description: 'Hai completato un task in meno di 1 ora',
    icon: '⚡',
    category: 'tasks',
    check: (stats) => stats.fastestTaskMinutes && stats.fastestTaskMinutes < 60,
  },
  {
    id: 'puntuale',
    name: 'Sempre Puntuale',
    description: 'Hai completato 5 task prima della deadline',
    icon: '⏰',
    category: 'tasks',
    check: (stats) => stats.tasksBeforeDeadline >= 5,
  },

  // === NEGOTIATIONS ===
  {
    id: 'primo_negoziato',
    name: 'Primo Negoziato',
    description: 'Hai negoziato la tua prima estensione',
    icon: '🤝',
    category: 'negotiations',
    check: (stats) => stats.totalNegotiations >= 1,
  },
  {
    id: 'diplomatico',
    name: 'Il Diplomatico',
    description: 'Hai vinto 5 negoziazioni',
    icon: '🎭',
    category: 'negotiations',
    check: (stats) => stats.wonNegotiations >= 5,
  },
  {
    id: 'corruttore',
    name: 'Il Corruttore',
    description: 'Hai offerto più di 100 punti di tangenti totali',
    icon: '💰',
    category: 'negotiations',
    check: (stats) => stats.totalBribePoints >= 100,
  },
  {
    id: 'pizzaiolo',
    name: 'Il Pizzaiolo',
    description: 'Hai offerto pizza 3 volte',
    icon: '🍕',
    category: 'negotiations',
    check: (stats) => stats.pizzaOffers >= 3,
  },
  {
    id: 'barista',
    name: 'Il Barista',
    description: 'Hai offerto caffè 5 volte',
    icon: '☕',
    category: 'negotiations',
    check: (stats) => stats.coffeeOffers >= 5,
  },
  {
    id: 'lucky',
    name: 'Fortunello',
    description: 'Hai vinto la sorpresa con 20+ punti',
    icon: '🎰',
    category: 'negotiations',
    check: (stats) => stats.luckyWin === true,
  },

  // === RISPETTO ===
  {
    id: 'rispetto_150',
    name: 'Uomo di Rispetto',
    description: 'Hai raggiunto 150 punti rispetto',
    icon: '🏆',
    category: 'rispetto',
    check: (stats) => stats.rispetto >= 150,
  },
  {
    id: 'rispetto_200',
    name: 'Consigliere',
    description: 'Hai raggiunto 200 punti rispetto',
    icon: '🎩',
    category: 'rispetto',
    check: (stats) => stats.rispetto >= 200,
  },
  {
    id: 'rispetto_250',
    name: 'Leggenda della Famiglia',
    description: 'Hai raggiunto 250 punti rispetto',
    icon: '👑',
    category: 'rispetto',
    check: (stats) => stats.rispetto >= 250,
  },
  {
    id: 'traditore_redento',
    name: 'Traditore Redento',
    description: 'Sei sceso sotto 25 rispetto e sei tornato sopra 100',
    icon: '🔄',
    category: 'rispetto',
    check: (stats) => stats.wasTraitor && stats.rispetto >= 100,
  },

  // === SPECIAL & FUNNY ===
  {
    id: 'prima_volta',
    name: 'Benvenuto nella Famiglia',
    description: 'Hai effettuato il tuo primo accesso',
    icon: '🤌',
    category: 'special',
    check: () => true, // Everyone gets this
  },
  {
    id: 'weekend_warrior',
    name: 'Weekend Warrior',
    description: 'Hai loggato ore di sabato o domenica',
    icon: '🏋️',
    category: 'special',
    check: (stats) => stats.weekendEntries > 0,
  },
  {
    id: 'multi_commessa',
    name: 'Multitasking',
    description: 'Hai lavorato su 5 commesse diverse',
    icon: '🎪',
    category: 'special',
    check: (stats) => stats.uniqueCommesse >= 5,
  },
  {
    id: 'chiacchierone',
    name: 'Il Chiacchierone',
    description: 'Hai usato il chat con Il Don 20 volte',
    icon: '💬',
    category: 'special',
    check: (stats) => stats.chatMessages >= 20,
  },
  {
    id: 'silenzioso',
    name: 'Uomo di Poche Parole',
    description: 'Hai usato solo Quick Entry per 10 volte consecutive',
    icon: '🤫',
    category: 'special',
    check: (stats) => stats.quickEntryStreak >= 10,
  },
];

/**
 * Hook to manage user achievements
 */
export const useAchievements = (userId) => {
  const [achievements, setAchievements] = useState([]);
  const [stats, setStats] = useState(null);
  const [newAchievement, setNewAchievement] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch user stats and calculate achievements
  const refreshAchievements = useCallback(async () => {
    if (!userId) return;

    setIsLoading(true);
    try {
      // Fetch stats from backend
      const response = await axios.get(`${API}/achievements/stats/${userId}`);
      const userStats = response.data;
      setStats(userStats);

      // Check which achievements are unlocked
      const unlocked = ACHIEVEMENTS.filter(a => a.check(userStats));

      // Check for new achievements
      const savedAchievements = JSON.parse(localStorage.getItem(`achievements_${userId}`) || '[]');
      const newlyUnlocked = unlocked.filter(a => !savedAchievements.includes(a.id));

      if (newlyUnlocked.length > 0) {
        // Save to localStorage
        const allUnlockedIds = unlocked.map(a => a.id);
        localStorage.setItem(`achievements_${userId}`, JSON.stringify(allUnlockedIds));

        // Trigger celebration for first new achievement
        setNewAchievement(newlyUnlocked[0]);
      }

      setAchievements(unlocked);
    } catch (error) {
      console.error('Error fetching achievements:', error);
      // Fallback: give them the welcome achievement at minimum
      setAchievements([ACHIEVEMENTS.find(a => a.id === 'prima_volta')]);
    }
    setIsLoading(false);
  }, [userId]);

  useEffect(() => {
    refreshAchievements();
  }, [refreshAchievements]);

  // Dismiss the new achievement popup
  const dismissNewAchievement = useCallback(() => {
    setNewAchievement(null);
  }, []);

  return {
    achievements,
    allAchievements: ACHIEVEMENTS,
    stats,
    newAchievement,
    dismissNewAchievement,
    isLoading,
    refreshAchievements,
    unlockedCount: achievements.length,
    totalCount: ACHIEVEMENTS.length,
    progress: Math.round((achievements.length / ACHIEVEMENTS.length) * 100),
  };
};

export default useAchievements;
